import React, { useEffect, useMemo, useState } from "react";
import { Select, MenuItem } from "@mui/material";
import { BarChart as StackBar } from "@mui/x-charts/BarChart";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
import {
  Pie,
  Doughnut,
  Bar,
  PolarArea,
  Bubble,
  Scatter,
  Radar,
  Line,
} from "react-chartjs-2";
import { getDashboardColors } from "../../utils/dashboardColors";
import {
  getSubjectQuizCompletedDetails,
  getSubjectTestCompletedDetails,
  getSubjectActiveRecallCompletedDetails,
  getSubjectAdaptiveLearningCompletedDetails,
} from "../../helper/apicalls/dashboard";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
);

const MasterySummaryCard = ({
  title,
  mastery = {},
  primaryLabel,
  chartType = "pie",
  subjectCategory = [],
  mode,
}) => {
  const {
    successColor,
    dangerColor,
    chartColors,
  } = getDashboardColors();
  const [selectedSubject, setSelectedSubject] = useState("overall");
  const [selectedTopic, setSelectedTopic] = useState("overall");
  const [subjectDetails, setSubjectDetails] = useState({});

  const subjectOptions = useMemo(() => {
    const fromProp = (subjectCategory || [])
      .map((subject) => ({
        key: subject?.subject_key ?? subject?.key,
        label: subject?.subject_name ?? subject?.name,
      }))
      .filter((s) => s.key);

    if (fromProp.length > 0) return fromProp;

    const keys = Object.keys(mastery || {}).filter((k) => k !== "overall");
    return keys
      .map((k) => ({
        key: k,
        label:
          mastery?.[k]?.subject_name ??
          mastery?.[k]?.name ??
          mastery?.[k]?.subject ??
          mastery?.[k]?.topic_name ??
          k,
      }))
      .filter((s) => s.key);
  }, [subjectCategory, mastery]);

  useEffect(() => {
    setSelectedTopic("overall");
  }, [selectedSubject]);

  useEffect(() => {
    const availableKeys = new Set(subjectOptions.map((s) => s.key));
    if (selectedSubject !== "overall" && !availableKeys.has(selectedSubject)) {
      setSelectedSubject("overall");
    }
  }, [subjectOptions, selectedSubject]);

  useEffect(() => {
    setSelectedSubject("overall");
  }, [chartType]);

  const selectedSubjectDetails =
    selectedSubject !== "overall" ? subjectDetails[selectedSubject] : null;

  const topics = useMemo(() => {
    if (
      ["adaptive_learning", "active_recall", "quiz", "test"].includes(mode) &&
      selectedSubjectDetails?.topics
    ) {
      return selectedSubjectDetails.topics;
    }
    return [];
  }, [mode, selectedSubjectDetails]);

  const selectedData = useMemo(() => {
    const selectedDataRaw =
      selectedSubject === "overall"
        ? mastery.overall || {}
        : mastery[selectedSubject] || {};

    let data = {
      ...selectedDataRaw,
      ...(selectedSubjectDetails || {}),
    };

    if (mode === "adaptive_learning" && selectedTopic !== "overall") {
      const topic = topics.find(
        (t) => t.topic_key === selectedTopic || t.key === selectedTopic,
      );
      if (topic) return topic;
    }
    return data;
  }, [
    mastery,
    selectedSubject,
    selectedSubjectDetails,
    mode,
    selectedTopic,
    topics,
  ]);

  useEffect(() => {
    if (!mode) return;

    if (chartType === "graph") {
      let fetchFn = null;
      switch (mode) {
        case "quiz":
          fetchFn = getSubjectQuizCompletedDetails;
          break;
        case "test":
          fetchFn = getSubjectTestCompletedDetails;
          break;
        case "active_recall":
          fetchFn = getSubjectActiveRecallCompletedDetails;
          break;
        case "adaptive_learning":
          fetchFn = getSubjectAdaptiveLearningCompletedDetails;
          break;
        default:
          fetchFn = null;
      }

      if (fetchFn && subjectCategory?.length > 0) {
        const missingSubjects = subjectCategory.filter(
          (s) => !mastery[s.subject_key] && !subjectDetails[s.subject_key],
        );

        if (missingSubjects.length > 0) {
          Promise.all(
            missingSubjects.map((s) => fetchFn({ key: s.subject_key })),
          )
            .then((results) => {
              const newDetails = {};
              results.forEach((data, index) => {
                const actualData = data?.data || data;
                if (actualData) {
                  newDetails[missingSubjects[index].subject_key] = actualData;
                }
              });
              setSubjectDetails((prev) => ({ ...prev, ...newDetails }));
            })
            .catch((err) =>
              console.error("Error fetching all subjects details:", err),
            );
        }
      }
      return;
    }

    if (selectedSubject === "overall") return;
    if (subjectDetails[selectedSubject]) return;
    if (mastery[selectedSubject]) return;

    let fetchFn = null;

    switch (mode) {
      case "quiz":
        fetchFn = getSubjectQuizCompletedDetails;
        break;
      case "test":
        fetchFn = getSubjectTestCompletedDetails;
        break;
      case "active_recall":
        fetchFn = getSubjectActiveRecallCompletedDetails;
        break;
      case "adaptive_learning":
        fetchFn = getSubjectAdaptiveLearningCompletedDetails;
        break;
      default:
        fetchFn = null;
    }

    if (!fetchFn) return;

    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const data = await fetchFn({ key: selectedSubject });
        if (controller.signal.aborted) return;
        setSubjectDetails((prev) => ({
          ...prev,
          [selectedSubject]: data?.data || data || {},
        }));
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Error fetching subject mastery details:", error);
        setSubjectDetails((prev) => ({
          ...prev,
          [selectedSubject]: {},
        }));
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [
    mode,
    selectedSubject,
    subjectDetails,
    chartType,
    subjectCategory,
    mastery,
  ]);

  const correct = selectedData.no_of_question_correct || 0;

  const attempted =
    selectedData.no_of_questions_attempted ||
    selectedData.total_questions ||
    (selectedData.no_of_question_correct || 0) +
      (selectedData.no_of_question_incorrect || 0);

  let totalItems = 0;
  let completedItems = 0;

  if (mode === "quiz") {
    totalItems = selectedData.total_quiz || 0;
    completedItems = selectedData.total_quiz_completed || 0;
  } else if (mode === "test") {
    totalItems = selectedData.total_test || 0;
    completedItems = selectedData.total_test_completed || 0;
  } else if (mode === "active_recall") {
    totalItems = selectedData.total_recall || 0;
    completedItems = selectedData.total_recall_completed || 0;
  } else if (mode === "adaptive_learning") {
    totalItems = selectedData.total_adaptive_learning || 0;
    completedItems = selectedData.total_adaptive_learning_completed || 0;
  } else {
    totalItems =
      selectedData.total_quiz ||
      selectedData.total_test ||
      selectedData.total_question_banks ||
      selectedData.total_questions ||
      0;

    completedItems =
      selectedData.total_quiz_completed ||
      selectedData.total_test_completed ||
      selectedData.completed_question_banks ||
      selectedData.completed_questions ||
      0;
  }

  const completionPercent = useMemo(() => {
    if (!totalItems) return 0;
    return Math.round((completedItems / totalItems) * 100);
  }, [completedItems, totalItems]);

  const accuracy = useMemo(() => {
    if (mode === "active_recall") {
      return completionPercent;
    }
    if (!attempted) return 0;
    return Math.round((correct / attempted) * 100);
  }, [attempted, correct, mode, completionPercent]);

  const incorrect =
    selectedData.no_of_question_incorrect !== undefined
      ? selectedData.no_of_question_incorrect
      : attempted > correct
        ? attempted - correct
        : 0;

  const remaining =
    totalItems > completedItems ? totalItems - completedItems : 0;

  const showNoData =
    accuracy === 0 &&
    completionPercent === 0 &&
    attempted === 0 &&
    completedItems === 0;

  const completionPieData = useMemo(
    () => ({
      labels: ["Correct", "Incorrect"],
      datasets: [
        {
          data: [correct || 0, incorrect || 0],
          backgroundColor: [chartColors[0], chartColors[1]],
          borderWidth: 0,
        },
      ],
    }),
    [correct, incorrect, chartColors],
  );

  const completionPieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          align: "center",
          labels: {
            boxWidth: 10,
            usePointStyle: true,
            font: {
              size: 12,
              weight: "600",
            },
          },
        },
        tooltip: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#111827",
          bodyColor: "#4b5563",
          borderColor: "#e5e7eb",
          borderWidth: 1,
          callbacks: {
            label: (ctx) => {
              const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
              const percentage =
                total > 0 ? Math.round((ctx.parsed / total) * 100) : 0;
              return `${ctx.label}: ${ctx.parsed} (${percentage}%)`;
            },
          },
        },
      },
    }),
    [],
  );

  const completionDoughnutOptions = useMemo(
    () => ({
      ...completionPieOptions,
      radius: "85%",
      cutout: "75%",
    }),
    [completionPieOptions],
  );

  const stackBarData = useMemo(() => {
    const isSubjectMode = selectedSubject === "overall";
    let categories = [];

    if (isSubjectMode) {
      const subjectKeys = Object.keys(mastery || {}).filter(
        (key) => key !== "overall",
      );

      categories = subjectKeys.map((key) => {
        const item = mastery[key] || {};
        return {
          key,
          ...item,
        };
      });
    } else {
      categories = topics || [];
    }

    if (categories.length > 0) {
      const labels = categories.map(
        (c) => c.subject_name || c.topic_name || c.name || c.label || "Unknown",
      );

      return {
        labels,
        datasets: [
          {
            label: "Attempted",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                const correctVal = d.no_of_question_correct || 0;
                const incorrectVal = d.no_of_question_incorrect || 0;
                return (
                  d.no_of_questions_attempted ||
                  d.total_questions ||
                  correctVal + incorrectVal ||
                  0
                );
              }
              const correctVal = c.no_of_question_correct || 0;
              const incorrectVal = c.no_of_question_incorrect || 0;
              return (
                c.no_of_questions_attempted ||
                c.total_questions ||
                correctVal + incorrectVal ||
                0
              );
            }),
            backgroundColor: chartColors[2],
            borderColor: chartColors[2],
            borderWidth: 0,
            maxBarThickness: 14,
          },
          {
            label: "Correct",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                return d.no_of_question_correct || 0;
              }
              return c.no_of_question_correct || 0;
            }),
            backgroundColor: chartColors[0],
            borderColor: chartColors[0],
            borderWidth: 0,
            maxBarThickness: 14,
          },
          {
            label: "Incorrect",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                if (d.no_of_question_incorrect !== undefined) {
                  return d.no_of_question_incorrect || 0;
                }
                const attemptedVal = d.no_of_questions_attempted || 0;
                const correctVal = d.no_of_question_correct || 0;
                return attemptedVal > correctVal
                  ? attemptedVal - correctVal
                  : 0;
              }
              if (c.no_of_question_incorrect !== undefined) {
                return c.no_of_question_incorrect || 0;
              }
              const attemptedVal = c.no_of_questions_attempted || 0;
              const correctVal = c.no_of_question_correct || 0;
              return attemptedVal > correctVal ? attemptedVal - correctVal : 0;
            }),
            backgroundColor: chartColors[1],
            borderColor: chartColors[1],
            borderWidth: 0,
            maxBarThickness: 14,
          },
        ],
      };
    }

    return {
      labels: ["No Data"],
      datasets: [
        {
          label: "Attempted",
          data: [0],
          backgroundColor: chartColors[2],
        },
        {
          label: "Correct",
          data: [0],
          backgroundColor: chartColors[0],
        },
        {
          label: "Incorrect",
          data: [0],
          backgroundColor: chartColors[1],
        },
      ],
    };
  }, [
    mastery,
    selectedSubject,
    topics,
    chartColors,
  ]);

  const performanceBarData = useMemo(() => {
    const isSubjectMode = selectedSubject === "overall";
    let categories = [];

    if (isSubjectMode) {
      const subjectKeys = Object.keys(mastery || {}).filter(
        (key) => key !== "overall",
      );

      categories = subjectKeys.map((key) => {
        const item = mastery[key] || {};
        return {
          key,
          ...item,
        };
      });
    } else {
      categories = topics || [];
    }

    if (categories.length > 0) {
      const truncateLabel = (value) => {
        const label = value || "";
        return label.length > 9 ? `${label.slice(0, 9)}…` : label;
      };

      const labels = categories.map((c) =>
        truncateLabel(c.subject_name || c.topic_name || c.name || c.label),
      );

      return {
        labels,
        datasets: [
          {
            label: "Attempted",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                const correctVal = d.no_of_question_correct || 0;
                const incorrectVal = d.no_of_question_incorrect || 0;
                return (
                  d.no_of_questions_attempted ||
                  d.total_questions ||
                  correctVal + incorrectVal ||
                  0
                );
              }
              const correctVal = c.no_of_question_correct || 0;
              const incorrectVal = c.no_of_question_incorrect || 0;
              return (
                c.no_of_questions_attempted ||
                c.total_questions ||
                correctVal + incorrectVal ||
                0
              );
            }),
            backgroundColor: chartColors[2],
            borderColor: chartColors[2],
            borderWidth: 0,
            maxBarThickness: 14,
          },
          {
            label: "Correct",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                return d.no_of_question_correct || 0;
              }
              return c.no_of_question_correct || 0;
            }),
            backgroundColor: chartColors[0],
            borderColor: chartColors[0],
            borderWidth: 0,
            maxBarThickness: 14,
          },
          {
            label: "Incorrect",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                if (d.no_of_question_incorrect !== undefined) {
                  return d.no_of_question_incorrect || 0;
                }
                const attemptedVal = d.no_of_questions_attempted || 0;
                const correctVal = d.no_of_question_correct || 0;
                return attemptedVal > correctVal
                  ? attemptedVal - correctVal
                  : 0;
              }
              if (c.no_of_question_incorrect !== undefined) {
                return c.no_of_question_incorrect || 0;
              }
              const attemptedVal = c.no_of_questions_attempted || 0;
              const correctVal = c.no_of_question_correct || 0;
              return attemptedVal > correctVal ? attemptedVal - correctVal : 0;
            }),
            backgroundColor: chartColors[1],
            borderColor: chartColors[1],
            borderWidth: 0,
            maxBarThickness: 14,
          },
        ],
      };
    }

    return {
      labels: ["Questions"],
      datasets: [
        {
          label: "Attempted",
          data: [attempted || 0],
          backgroundColor: chartColors[2],
          borderColor: chartColors[2],
          borderWidth: 0,
          maxBarThickness: 14,
        },
        {
          label: "Correct",
          data: [correct || 0],
          backgroundColor: chartColors[0],
          borderColor: chartColors[0],
          borderWidth: 0,
          maxBarThickness: 14,
        },
        {
          label: "Incorrect",
          data: [incorrect || 0],
          backgroundColor: chartColors[1],
          borderColor: chartColors[1],
          borderWidth: 0,
          maxBarThickness: 14,
        },
      ],
    };
  }, [
    attempted,
    correct,
    incorrect,
    mastery,
    selectedSubject,
    topics,
    chartColors,
  ]);

  const performanceBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      datasets: {
        bar: {
          categoryPercentage: 0.6,
          barPercentage: 0.55,
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          align: "center",
          labels: {
            boxWidth: 15,
            boxHeight: 15,
            usePointStyle: false,
            padding: 15,
            font: {
              size: 12,
              weight: "500",
            },
            color: "#637381",
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#111827",
          bodyColor: "#4b5563",
          borderColor: "#e5e7eb",
          borderWidth: 1,
          callbacks: {
            title: (ctx) => {
              const idx = ctx?.[0]?.dataIndex ?? 0;
              const labels = performanceBarData.labels || [];
              return labels[idx] || "";
            },
            label: (ctx) => `${ctx.parsed.y}% Complete`,
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: true,
            borderDash: [3, 3],
            color: "#E5E7EB",
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "#E5E7EB",
            borderDash: [3, 3],
          },
          ticks: {
            precision: 0,
          },
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
    }),
    [performanceBarData.labels],
  );

  const progressStackData = useMemo(() => {
    const isSubjectMode = selectedSubject === "overall";
    let categories = [];

    if (isSubjectMode) {
      const subjectKeys = Object.keys(mastery || {}).filter(
        (key) => key !== "overall",
      );

      categories = subjectKeys.map((key) => {
        const item = mastery[key] || {};
        return {
          key,
          ...item,
        };
      });
    } else {
      categories = topics || [];
    }

    const getAttemptedCorrect = (item) => {
      const attemptedVal =
        item.no_of_questions_attempted ??
        item.total_questions ??
        (item.no_of_question_correct || 0) +
          (item.no_of_question_incorrect || 0);

      const correctVal = item.no_of_question_correct ?? 0;

      if (
        mode === "active_recall" ||
        mode === "quiz" ||
        mode === "test" ||
        mode === "adaptive_learning"
      ) {
        return {
          attempted: attemptedVal || 0,
          correct: correctVal || 0,
        };
      }

      return {
        attempted: attemptedVal || 0,
        correct: correctVal || 0,
      };
    };

    const labels =
      categories.length > 0
        ? categories.map(
            (c) =>
              c.subject_name || c.topic_name || c.name || c.label || "Unknown",
          )
        : ["Overall"];

    const totals =
      categories.length > 0
        ? categories.map((c) => {
            if (isSubjectMode) {
              const d = mastery[c.key] || {};
              return getAttemptedCorrect(d);
            }
            return getAttemptedCorrect(c);
          })
        : [getAttemptedCorrect(selectedData || {})];

    const correctVals = totals.map((t) => t.correct || 0);
    const attemptedRemainderVals = totals.map((t) => {
      const attemptedVal = t.attempted || 0;
      const correctVal = t.correct || 0;
      return attemptedVal > correctVal ? attemptedVal - correctVal : 0;
    });

    return {
      labels,
      datasets: [
        {
          label: "Correct",
          data: correctVals,
          backgroundColor: chartColors[0],
          borderWidth: 0,
          maxBarThickness: 18,
          stack: "progress",
        },
        {
          label: "Incorrect",
          data: attemptedRemainderVals,
          backgroundColor: chartColors[1],
          borderWidth: 0,
          maxBarThickness: 18,
          stack: "progress",
        },
      ],
    };
  }, [mastery, selectedSubject, topics, mode, selectedData, chartColors]);

  const progressStackOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          align: "center",
          labels: {
            boxWidth: 15,
            boxHeight: 15,
            padding: 15,
            font: {
              size: 12,
              weight: "500",
            },
            color: "#637381",
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#111827",
          bodyColor: "#4b5563",
          borderColor: "#e5e7eb",
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: true,
            borderDash: [3, 3],
            color: "#E5E7EB",
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: {
            color: "#E5E7EB",
            borderDash: [3, 3],
          },
          ticks: {
            precision: 0,
          },
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
    }),
    [],
  );

  const polarData = useMemo(
    () => ({
      labels: ["Correct", "Incorrect", "Remaining"],
      datasets: [
        {
          data: [correct || 0, incorrect || 0, remaining || 0],
          backgroundColor: [chartColors[0], chartColors[1], chartColors[2]],
          borderWidth: 0,
        },
      ],
    }),
    [correct, incorrect, remaining, chartColors],
  );

  const polarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: { boxWidth: 10, usePointStyle: true },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.label}: ${ctx.parsed || 0}`,
          },
        },
      },
      scales: {
        r: {
          grid: { color: "#e5e7eb" },
          angleLines: { color: "#e5e7eb" },
          ticks: { display: false },
        },
      },
    }),
    [],
  );

  const radarData = useMemo(() => {
    const isSubjectMode = selectedSubject === "overall";
    let categories = [];

    if (isSubjectMode) {
      const subjectKeys = Object.keys(mastery || {}).filter(
        (key) => key !== "overall",
      );

      categories = subjectKeys.map((key) => {
        const item = mastery[key] || {};
        return {
          key,
          ...item,
        };
      });
    } else {
      categories = topics || [];
    }

    if (categories.length > 0) {
      return {
        labels: categories.map(
          (c) => c.subject_name || c.topic_name || c.name || c.label,
        ),
        datasets: [
          {
            label: "Correct",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                return d.no_of_question_correct || 0;
              }
              return c.no_of_question_correct || 0;
            }),
            backgroundColor: "rgba(153, 226, 200, 0.18)",
            borderColor: successColor,
          },
          {
            label: "Incorrect",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                const attemptedVal =
                  d.no_of_questions_attempted ||
                  d.total_questions ||
                  (d.no_of_question_correct || 0) +
                    (d.no_of_question_incorrect || 0);
                const correctVal = d.no_of_question_correct || 0;
                return attemptedVal > correctVal
                  ? attemptedVal - correctVal
                  : 0;
              }
              const attemptedVal =
                c.no_of_questions_attempted ||
                c.total_questions ||
                (c.no_of_question_correct || 0) +
                  (c.no_of_question_incorrect || 0);
              const correctVal = c.no_of_question_correct || 0;
              return attemptedVal > correctVal ? attemptedVal - correctVal : 0;
            }),
            backgroundColor: "rgba(255, 179, 193, 0.16)",
            borderColor: dangerColor,
          },
        ],
      };
    }

    return {
      labels: ["Total Questions", "Correct", "Incorrect"],
      datasets: [
        {
          label: "Performance",
          data: [correct, incorrect],
          backgroundColor: "rgba(153, 209, 242, 0.15)",
          pointBackgroundColor: [chartColors[0], chartColors[1]],
          pointBorderColor: "#ffffff",
          pointRadius: 4,
          borderWidth: 2,
        },
      ],
    };
  }, [
    correct,
    incorrect,
    mastery,
    selectedSubject,
    topics,
    chartColors,
    dangerColor,
    successColor,
  ]);

  const radarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.parsed.r || 0}%`,
          },
        },
      },
      scales: {
        r: {
          beginAtZero: true,
          ticks: { display: false },
          grid: { color: "#E5E7EB" },
          angleLines: { color: "#E5E7EB" },
        },
      },
    }),
    [],
  );

  const scatterData = useMemo(
    () => ({
      datasets: [
        {
          label: "Correct vs Total",
          data: [
            {
              x: attempted || 0,
              y: correct || 0,
            },
          ],
          backgroundColor: successColor,
        },
        {
          label: "Incorrect vs Total",
          data: [
            {
              x: attempted || 0,
              y: incorrect || 0,
            },
          ],
          backgroundColor: dangerColor,
        },
      ],
    }),
    [attempted, correct, incorrect, dangerColor, successColor],
  );

  const scatterOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `Total: ${ctx.parsed.x || 0}, Completed: ${ctx.parsed.y || 0}`,
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { display: false },
          title: { display: true, text: "Total" },
        },
        y: {
          beginAtZero: true,
          grid: { color: "#E5E7EB" },
          title: { display: true, text: "Completed" },
        },
      },
    }),
    [],
  );

  const bubbleData = useMemo(
    () => ({
      datasets: [
        {
          label: "Question Performance",
          data: [
            {
              x: attempted || 0,
              y: correct || 0,
              r: 10,
            },
            {
              x: attempted || 0,
              y: incorrect || 0,
              r: 8,
            },
          ],
          backgroundColor: [chartColors[0], chartColors[1]],
        },
      ],
    }),
    [attempted, correct, incorrect, chartColors],
  );

  const bubbleOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.raw || {};
              return `Accuracy: ${v.x || 0}%, Completion: ${v.y || 0}%`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: { display: true, text: "Total Questions" },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: "Count" },
        },
      },
    }),
    [],
  );

  const lineData = useMemo(() => {
    const isSubjectMode = selectedSubject === "overall";
    let categories = [];

    if (isSubjectMode) {
      const subjectKeys = Object.keys(mastery || {}).filter(
        (key) => key !== "overall",
      );

      categories = subjectKeys.map((key) => {
        const item = mastery[key] || {};
        return {
          key,
          ...item,
        };
      });
    } else {
      categories = topics || [];
    }

    if (categories.length > 0) {
      return {
        labels: categories.map(
          (c) => c.subject_name || c.topic_name || c.name || c.label,
        ),
        datasets: [
          {
            label: "Correct",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                return d.no_of_question_correct || 0;
              }
              return c.no_of_question_correct || 0;
            }),
            borderColor: successColor,
            backgroundColor: "rgba(153, 226, 200, 0.20)",
            tension: 0.4,
            cubicInterpolationMode: "monotone",
            pointBackgroundColor: successColor,
            pointBorderColor: "#fff",
            pointRadius: 5,
            pointHoverRadius: 7,
            fill: true,
          },
          {
            label: "Incorrect",
            data: categories.map((c) => {
              if (isSubjectMode) {
                const d = mastery[c.key] || {};
                const attemptedVal =
                  d.no_of_questions_attempted ||
                  d.total_questions ||
                  (d.no_of_question_correct || 0) +
                    (d.no_of_question_incorrect || 0);
                const correctVal = d.no_of_question_correct || 0;
                return attemptedVal > correctVal
                  ? attemptedVal - correctVal
                  : 0;
              }
              const attemptedVal =
                c.no_of_questions_attempted ||
                c.total_questions ||
                (c.no_of_question_correct || 0) +
                  (c.no_of_question_incorrect || 0);
              const correctVal = c.no_of_question_correct || 0;
              return attemptedVal > correctVal ? attemptedVal - correctVal : 0;
            }),
            borderColor: dangerColor,
            backgroundColor: "rgba(255, 179, 193, 0.16)",
            tension: 0.4,
            cubicInterpolationMode: "monotone",
            pointBackgroundColor: dangerColor,
            pointBorderColor: "#fff",
            pointRadius: 5,
            pointHoverRadius: 7,
            fill: true,
          },
        ],
      };
    }

    return {
      labels: ["Overall"],
      datasets: [
        {
          label: "Correct",
          data: [correct],
          backgroundColor: "rgba(153, 209, 242, 0.15)",
          tension: 0.4,
          cubicInterpolationMode: "monotone",
          pointBackgroundColor: successColor,
          pointBorderColor: "#fff",
          pointRadius: 5,
          pointHoverRadius: 7,
          fill: true,
        },
        {
          label: "Incorrect",
          data: [incorrect],
          borderColor: dangerColor,
          backgroundColor: "rgba(255, 179, 193, 0.16)",
          tension: 0.4,
          cubicInterpolationMode: "monotone",
          pointBackgroundColor: dangerColor,
          pointBorderColor: "#fff",
          pointRadius: 5,
          pointHoverRadius: 7,
          fill: true,
        },
      ],
    };
  }, [
    mastery,
    correct,
    incorrect,
    selectedSubject,
    topics,
    dangerColor,
    successColor,
  ]);

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            boxWidth: 10,
            usePointStyle: true,
            padding: 20,
            font: { size: 12 },
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          titleColor: "#333",
          bodyColor: "#666",
          borderColor: "#ddd",
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          grid: {
            display: true,
            borderDash: [5, 5],
            color: "#E5E7EB",
          },
          ticks: {
            font: { size: 10 },
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
            borderDash: [5, 5],
            color: "#E5E7EB",
          },
          ticks: {
            font: { size: 10 },
          },
        },
      },
    }),
    [],
  );

  const accuracyColor =
    accuracy >= 70
      ? { bg: "#E8F5E9", text: "#2E7D32", border: "#A5D6A7" }
      : accuracy >= 40
        ? { bg: "#FFF8E1", text: "#F57F17", border: "#FFE082" }
        : { bg: "#FFEBEE", text: "#C62828", border: "#EF9A9A" };

  return (
    <div className="card-styled-wrapper mastery-overview-card">
      <div className="mastery-overview-card__content">
        <div className="mastery-overview-header">
          <p className="mastery-overview-title">{title}</p>
          <div className="mastery-overview-header-right">
            <div
              className="mastery-accuracy-chip"
              style={{
                background: accuracyColor.bg,
                color: accuracyColor.text,
              }}
            >
              <span className="mastery-accuracy-chip__label">Accuracy</span>
              <span className="mastery-accuracy-chip__value">{accuracy}%</span>
            </div>
          </div>
        </div>

        {subjectOptions.length > 0 && (
          <div className="mastery-overview-subject-row">
            <Select
              value={selectedSubject}
              size="small"
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="mastery-overview-subject-select"
              displayEmpty
              title={
                selectedSubject === "overall"
                  ? "Overall"
                  : subjectOptions.find((s) => s.key === selectedSubject)
                      ?.label || ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "var(--dashboard-metric-card-bg)",
                  border: "2px solid var(--dashboard-progress-track)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    borderColor: "var(--dashboard-accent-color)",
                    backgroundColor: "var(--white-background)",
                    transform: "translateY(-1px)",
                    boxShadow:
                      "0 4px 12px rgba(var(--dashboard-accent-color-rgb), 0.15)",
                  },
                  "&.Mui-focused": {
                    borderColor: "var(--dashboard-accent-color)",
                    boxShadow:
                      "0 0 0 4px rgba(var(--dashboard-accent-color-rgb), 0.1), 0 4px 12px rgba(var(--dashboard-accent-color-rgb), 0.15)",
                    backgroundColor: "var(--white-background)",
                    transform: "translateY(-1px)",
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
                "& .MuiSelect-select": {
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: "var(--dashboard-heading-color)",
                  padding: "12px 16px",
                },
                "& .MuiSvgIcon-root": {
                  color: "var(--dashboard-accent-color)",
                  fontSize: "1.2rem",
                },
              }}
            >
              <MenuItem value="overall">Overall</MenuItem>
              {subjectOptions.map((subject) => (
                <MenuItem
                  key={subject.key}
                  value={subject.key}
                  sx={{
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor:
                        "rgba(var(--dashboard-accent-color-rgb), 0.08)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "var(--dashboard-accent-color)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "var(--dashboard-accent-color)",
                      },
                    },
                  }}
                >
                  {subject.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}

        <div className="mastery-overview-chart-wrap">
          {showNoData ? (
            <div className="mastery-overview-no-data">No data available</div>
          ) : (
            <div
              className={`mastery-overview-chart${
                ["pie", "doughnut", "polar"].includes(chartType)
                  ? " mastery-overview-chart--circular"
                  : ""
              }`}
            >
              {chartType === "pie" && (
                <Pie data={completionPieData} options={completionPieOptions} />
              )}
              {chartType === "doughnut" && (
                <Doughnut
                  data={completionPieData}
                  options={completionDoughnutOptions}
                />
              )}
              {chartType === "bar" && (
                <Bar
                  data={performanceBarData}
                  options={performanceBarOptions}
                />
              )}
              {chartType === "stack" && (
                <Bar
                  data={progressStackData}
                  options={progressStackOptions}
                />
              )}
              {chartType === "stackbar" && (
                <StackBar
                  height={260}
                  series={stackBarData.datasets.map((ds) => ({
                    data: ds.data,
                    label: ds.label,
                    stack: "total",
                    color: Array.isArray(ds.backgroundColor)
                      ? ds.backgroundColor[0]
                      : ds.backgroundColor,
                  }))}
                  xAxis={[
                    {
                      data: stackBarData.labels,
                      scaleType: "band",
                    },
                  ]}
                  margin={{
                    top: 24,
                    right: 16,
                    bottom: stackBarData.labels.some(
                      (label) => label.length > 12,
                    )
                      ? 100
                      : 80,
                    left: 60,
                  }}
                  bottomAxis={{
                    tickLabelStyle: {
                      fontSize: 11,
                      angle: stackBarData.labels.some(
                        (label) => label.length > 12,
                      )
                        ? -45
                        : 0,
                      textAnchor: stackBarData.labels.some(
                        (label) => label.length > 12,
                      )
                        ? "end"
                        : "middle",
                    },
                  }}
                  topAxis={null}
                  leftAxis={{
                    tickLabelStyle: {
                      fontSize: 11,
                    },
                  }}
                  slotProps={{
                    legend: {
                      direction: "row",
                      position: { vertical: "bottom", horizontal: "middle" },
                      padding: { top: 10, bottom: 10 },
                      labelStyle: {
                        fontSize: 12,
                      },
                    },
                  }}
                />
              )}
              {chartType === "polar" && (
                <PolarArea data={polarData} options={polarOptions} />
              )}
              {chartType === "radar" && (
                <Radar data={radarData} options={radarOptions} />
              )}
              {chartType === "scatter" && (
                <Scatter data={scatterData} options={scatterOptions} />
              )}
              {chartType === "bubble" && (
                <Bubble data={bubbleData} options={bubbleOptions} />
              )}
              {chartType === "line" && (
                <Line data={lineData} options={lineOptions} />
              )}
            </div>
          )}
        </div>

        {/* {!showNoData && (
          <div className="mastery-overview-stats-row">
            <div className="mastery-stat-pill mastery-stat-pill--neutral">
              <span className="mastery-stat-pill__val">{attempted}</span>
              <span className="mastery-stat-pill__key">Attempted</span>
            </div>
            <div className="mastery-stat-pill mastery-stat-pill--positive">
              <span className="mastery-stat-pill__val">{correct}</span>
              <span className="mastery-stat-pill__key">Correct</span>
            </div>
            <div className="mastery-stat-pill mastery-stat-pill--negative">
              <span className="mastery-stat-pill__val">{incorrect}</span>
              <span className="mastery-stat-pill__key">Incorrect</span>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default MasterySummaryCard;
